import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApproveMembershipRequestModel,
  ApproveMembershipRequestResponse,
  CancelMembershipRequestResponse,
  CreateMembershipRequestModel,
  GetClubVisibleMembershipPlansParams,
  GetClubVisibleMembershipPlansResponse,
  GetMembershipRequestsResponse,
  MembershipRequestDTO,
  MembershipRequestListParams,
  RejectMembershipRequestModel,
  RejectMembershipRequestResponse,
  SubmitMembershipRequestResponse,
  MembershipRequestStatus,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import { CLUB_MEMBERSHIP_QUERY_KEY, CLUB_MEMBERS_QUERY_KEY, MY_CLUBS_QUERY_KEY } from "../clubs/queryKeys";
import { VALIDATE_MEMBERSHIP_QUERY_KEY } from "../members/useMemberHooks";

export const MEMBERSHIP_REQUESTS_QUERY_KEY = "membership-requests";
export const CLUB_VISIBLE_MEMBERSHIP_PLANS_QUERY_KEY = "club-visible-membership-plans";
export const MEMBERSHIP_REQUEST_BY_ID_QUERY_KEY = "membership-request-by-id";

type MutationSuccessHandler<TData, TVariables> = NonNullable<
  UseMutationOptions<TData, Error, TVariables>["onSuccess"]
>;

const withOnSuccess = <TData, TVariables>(
  base: MutationSuccessHandler<TData, TVariables>,
  extra?: MutationSuccessHandler<TData, TVariables>,
): MutationSuccessHandler<TData, TVariables> => {
  return (data, variables, onMutateResult, context) => {
    base(data, variables, onMutateResult, context);
    return extra?.(data, variables, onMutateResult, context);
  };
};

export function useClubVisibleMembershipPlansQuery(
  client: AxiosInstance,
  clubId: number,
  params: GetClubVisibleMembershipPlansParams = {},
  options?: Omit<UseQueryOptions<GetClubVisibleMembershipPlansResponse>, "queryKey" | "queryFn">,
) {
  const { page = 0, pageSize = 10, search = "" } = params;

  return useQuery({
    queryKey: [CLUB_VISIBLE_MEMBERSHIP_PLANS_QUERY_KEY, clubId, page, pageSize, search],
    queryFn: async () => {
      const { getClubVisibleMembershipPlans } = await import("@membentry/shared-api");
      return getClubVisibleMembershipPlans(client, clubId, { page, pageSize, search });
    },
    enabled: clubId > 0,
    ...options,
  });
}

export function useMyMembershipRequestsQuery(
  client: AxiosInstance,
  params: MembershipRequestListParams = {},
  options?: Omit<UseQueryOptions<GetMembershipRequestsResponse>, "queryKey" | "queryFn">,
) {
  const { page = 0, pageSize = 10, clubId, status } = params;

  return useQuery({
    queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "my", clubId ?? null, status ?? null, page, pageSize],
    queryFn: async () => {
      const { getMyMembershipRequests } = await import("@membentry/shared-api");
      return getMyMembershipRequests(client, { page, pageSize, clubId, status });
    },
    ...options,
  });
}

export function useOwnerMembershipRequestsQuery(
  client: AxiosInstance,
  params: MembershipRequestListParams = {},
  options?: Omit<UseQueryOptions<GetMembershipRequestsResponse>, "queryKey" | "queryFn">,
) {
  const { page = 0, pageSize = 10, clubId, status } = params;

  return useQuery({
    queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "owner", clubId ?? null, status ?? null, page, pageSize],
    queryFn: async () => {
      const { getOwnerMembershipRequests } = await import("@membentry/shared-api");
      return getOwnerMembershipRequests(client, { page, pageSize, clubId, status });
    },
    ...options,
  });
}

export function useMembershipRequestByIdQuery(
  client: AxiosInstance,
  requestId: number,
  options?: Omit<UseQueryOptions<SubmitMembershipRequestResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: [MEMBERSHIP_REQUEST_BY_ID_QUERY_KEY, requestId],
    queryFn: async () => {
      const { getMembershipRequestById } = await import("@membentry/shared-api");
      return getMembershipRequestById(client, requestId);
    },
    enabled: requestId > 0,
    ...options,
  });
}

export interface OwnerMembershipRequestsOptions {
  clubId?: number;
  status?: MembershipRequestStatus;
}

export function useSubmitMembershipRequestMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<SubmitMembershipRequestResponse, Error, CreateMembershipRequestModel>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateMembershipRequestModel) => {
      const { submitMembershipRequest } = await import("@membentry/shared-api");
      return submitMembershipRequest(client, body);
    },
    onSuccess: withOnSuccess((data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "my"] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "owner", variables.clubId] });
      queryClient.invalidateQueries({ queryKey: [CLUB_VISIBLE_MEMBERSHIP_PLANS_QUERY_KEY, variables.clubId] });
      const userId = data.payload?.memberUserId;
      if (userId) {
        queryClient.invalidateQueries({ queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, userId] });
      }
    }, options?.onSuccess),
    ...options,
  });
}

type ReviewRequestVariables = {
  requestId: number;
  body: ApproveMembershipRequestModel;
  idempotencyKey?: string;
};

export function useApproveMembershipRequestMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<ApproveMembershipRequestResponse, Error, ReviewRequestVariables>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, body, idempotencyKey }: ReviewRequestVariables) => {
      const { approveMembershipRequest } = await import("@membentry/shared-api");
      return idempotencyKey
        ? approveMembershipRequest(client, requestId, body, { idempotencyKey })
        : approveMembershipRequest(client, requestId, body);
    },
    onSuccess: withOnSuccess((data) => {
      invalidateMembershipRequestSurfaces(queryClient, data.payload);
    }, options?.onSuccess),
    ...options,
  });
}

type RejectRequestVariables = {
  requestId: number;
  body: RejectMembershipRequestModel;
};

export function useRejectMembershipRequestMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<RejectMembershipRequestResponse, Error, RejectRequestVariables>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, body }: RejectRequestVariables) => {
      const { rejectMembershipRequest } = await import("@membentry/shared-api");
      return rejectMembershipRequest(client, requestId, body);
    },
    onSuccess: withOnSuccess((data) => {
      invalidateMembershipRequestSurfaces(queryClient, data.payload);
    }, options?.onSuccess),
    ...options,
  });
}

export function useCancelMembershipRequestMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<CancelMembershipRequestResponse, Error, number>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: number) => {
      const { cancelMembershipRequest } = await import("@membentry/shared-api");
      return cancelMembershipRequest(client, requestId);
    },
    onSuccess: withOnSuccess((data) => {
      invalidateMembershipRequestSurfaces(queryClient, data.payload);
    }, options?.onSuccess),
    ...options,
  });
}

export function invalidateMembershipRequests(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY] });
}

function invalidateMembershipRequestSurfaces(
  queryClient: ReturnType<typeof useQueryClient>,
  payload?: MembershipRequestDTO | null,
) {
  queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "my"] });
  queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "owner"] });

  if (!payload) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_REQUESTS_QUERY_KEY, "owner", payload.clubId] });
  queryClient.invalidateQueries({ queryKey: [CLUB_VISIBLE_MEMBERSHIP_PLANS_QUERY_KEY, payload.clubId] });

  if (payload.memberUserId) {
    queryClient.invalidateQueries({ queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, payload.memberUserId] });
  }

  if (payload.status === "Approved") {
    queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERS_QUERY_KEY, payload.clubId] });
    queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERSHIP_QUERY_KEY, payload.clubId] });
    queryClient.invalidateQueries({ queryKey: [MY_CLUBS_QUERY_KEY] });
  }

  if (payload.status === "Cancelled" || payload.status === "Rejected") {
    queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERS_QUERY_KEY, payload.clubId] });
  }
}
